import { useMemo, useState } from "react";
import Carousel from "components/common/Carousel";
import { useLifeChoicePageContext } from "context/hooks/useLifeChoicePageContext";
import useMediaQuery from "hooks/useMediaQuery";
import {
  ApiResponse,
  lifeChoiseDetailsDesktop,
  lifeChoiseDetailsMobile,
  lifeChoiseDetailsTablet,
} from "constantVariables";
import Occupation from "./Occupation";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getCareerTabData,
  getSettings,
  setActiveCareersSliders,
  setActiveCareerSuggestions,
} from "store/slices";
import Suggestion from "./Occupation/Suggestion";
import { t } from "i18next";
import { getCareers } from "api/career";
import { decrypt } from "utils/crypto";

const Occupations = () => {
  const { lifeChoice } = useLifeChoicePageContext();
  const isDesktop = useMediaQuery(lifeChoiseDetailsDesktop);
  const isMobile = useMediaQuery(lifeChoiseDetailsMobile);
  const isTablet = useMediaQuery(lifeChoiseDetailsTablet);
  const { language, dir } = useAppSelector(getSettings);
  const dispatch = useAppDispatch();
  const [activeCareer, setActiveCareer] = useState(null);
  const { activeCareersSliders } = useAppSelector(getCareerTabData);

  const [occupations, setOccupations] = useState([lifeChoice.related_careers]);

  const carouselOptions = useMemo(() => {
    let options = {
      slidesPerView: 5,
      slideToClickedSlide: true,
      centeredSlides: true,
      navigation: true,
      loop: false,
    };
    if (isDesktop) {
      options = {
        ...options,
        slidesPerView: 5,
      };
    } else if (isTablet) {
      options = {
        ...options,
        slidesPerView: 3,
      };
    } else if (isMobile) {
      options = {
        ...options,
        slidesPerView: 2,
      };
    }
    return options;
  }, [isDesktop, isMobile, isTablet]);

  const onClickCareer = async (item, idx) => {
    if (!idx) idx = 0;

    const newActiveSLider = {
      [idx]: item._id,
    };
    dispatch(setActiveCareersSliders(newActiveSLider));
    setActiveCareer(item);

    let newArr = [...occupations];

    if (!item.next_careers.length)
      return setOccupations([...newArr.filter((_, iIdx) => iIdx <= idx)]);

    newArr = newArr.filter((_, iIdx) => iIdx <= idx);

    if (typeof item.next_careers[0] === "string") {
      let arr = [];
      const res = await getCareers(item.next_careers);

      if (res.statusCode === ApiResponse.SUCCESS) {
        let data = decrypt(res.data);
        dispatch(setActiveCareerSuggestions(data));
        arr = data;
      }

      if (!arr.length)
        return setOccupations([...newArr.filter((_, iIdx) => iIdx <= idx)]);

      setOccupations([...newArr, arr]);

      return;
    }

    setOccupations([...newArr, item.occupations]);
  };

  const renderItem = (item, idx) => {
    return (
      <Occupation
        idx={idx}
        item={item}
        onClick={() => onClickCareer(item, idx)}
      />
    );
  };

  const renderSuggestionUI = (renderedItem, carouselIdx) => {
    if (!renderedItem?._id || !activeCareer?._id) return "";
    if (renderedItem?._id !== activeCareer?._id) return "";
    const activeSliderIndex = Object.keys(activeCareersSliders ?? {})[0];
    if (parseInt(carouselIdx) !== parseInt(activeSliderIndex)) return "";
    return (
      <div className="-mt-10 lg:-mt-12">
        <Suggestion
          lastSlide={renderedItem?.next_careers?.length === 0}
          activeCareer={activeCareer}
          parent={renderedItem._id}
        />
      </div>
    );
  };

  return (
    <div className="occupations">
      <p className={"text-theme-blue w-fit mx-auto text-xl"}>
        {t("funnelHeading")}
      </p>
      <h1
        lang={language}
        dir={dir}
        className="text-theme-blue font-semibold text-3xl w-fit mx-auto mt-2 mb-10"
      >
        {lifeChoice.title[language]}
      </h1>
      {occupations.map((ele, idx) => {
        return (
          <div key={idx}>
            <p
              className={`${
                idx === 0
                  ? "text-theme-yellow font-medium text-xl"
                  : "text-theme-gray-600"
              } w-fit mx-auto`}
            >
              {idx === 0 ? t("jobPossibilities") : ""}
            </p>
            {idx !== 0 &&
            idx === occupations.length - 1 &&
            activeCareer &&
            ele.every(
              (e) => e._id.toString() !== activeCareer._id.toString()
            ) ? (
              <div className="h-28 bg-[#DEE2FB] rounded-br-[16px] rounded-bl-[16px] flex items-center justify-center"></div>
            ) : (
              ""
            )}
            <Carousel
              key={`--carousel-occupation-${idx}`}
              items={ele}
              renderItem={renderItem}
              onSlideChange={(ele) => {
                if (
                  activeCareer._id.toString() ===
                  occupations[idx][ele.realIndex]._id.toString()
                )
                  return;
                onClickCareer(occupations[idx][ele.realIndex], idx);
              }}
              idx={idx}
              className={`!py-2 ${
                idx !== 0 &&
                idx === occupations.length - 1 &&
                activeCareer &&
                ele.every(
                  (e) => e._id.toString() !== activeCareer._id.toString()
                )
                  ? "-mt-16"
                  : ""
              }`}
              options={
                ele.length < carouselOptions.slidesPerView
                  ? {
                      ...carouselOptions,
                      loop: false,
                    }
                  : carouselOptions
              }
              renderUI={renderSuggestionUI}
            />
          </div>
        );
      })}
      {activeCareer?.next_careers?.length > 0 ? (
        <div className={"flex flex-col gap-4 items-center justify-center"}>
          <p className={"triangle"}></p>
          <p className="text-xs text-[#B5BDF2]">{t("clickForMore")}</p>
        </div>
      ) : (
        ""
      )}
      <p className="text-xs md:text-sm text-gray-400 italic text-center mt-4">{t("careerDisclamierText")}</p>
    </div>
  );
};

export default Occupations;
